import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { MemberLabel } from "./MemberLabel"

const memberButtonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 transition-colors duration-500 cursor-pointer",
  {
    variants: {
      variant: {
        default: "transition-all text-[#EDDBC0] bg-gradient-to-br from-[#a08669] to-[#553a20] hover:from-[#a08669]/90 hover:to-[#553a20]/90",
        outline: "transition-all text-[#EDDBC0] border border-2 border-[#7D674F] hover:bg-[#7D674F]/10"
      },
      size: {
        default: "h-10 rounded-md text-md px-4 py-2",
        xxs: "h-4 text-[8px] leading-4 py-0 px-1.5",
        sm: "h-8 text-xs px-2",
        lg: "h-12 px-8",
        '3xl': "h-[58px] px-8 text-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof memberButtonVariants> {
  asChild?: boolean
}

const MemberButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(
          "whitespace-nowrap",
          memberButtonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      >
        {
          variant === 'outline' ? (
            <span>
              <MemberLabel>{children}</MemberLabel>
            </span>
          ) : children
        }
      </Comp>
    )
  }
)
MemberButton.displayName = "MemberButton"

export { MemberButton, memberButtonVariants }
